// @flow

import React from "react";
import style from "./style.module.scss";
import { t } from "ttag";

import { Link } from "gatsby";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { createContentfulRenderer, CONTENTFUL_TYPES } from "../types";
import { Typography } from "@2po-c21/components";
import Render from "../.";
import { Container, Button } from "reactstrap";
import { Slider } from "../../../components/TrustPilot";
import { Section } from "@containers";
import { AgencyInformation } from "@components";

const Bold = ({ children }: *) => (
    <span className={style.bold}>{children}</span>
);

const Heading = ({ children }: *) => (
    <div className={style.heading}>
        <Typography type="heading-4-bold">{children}</Typography>
    </div>
);

const richTextBuilder = (locale, selectedAgencyData) => ({
    /* eslint-disable */
    renderMark: {
        [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
    },
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => {
            // Get the raw text content
            const rawText = node.content
                .map((content) => content.value || "")
                .join("");

            // If the paragraph contains our placeholder
            if (rawText.includes("${agencyName}")) {
                // Replace the placeholder with the actual agency name
                const processedChildren = React.Children.map(
                    children,
                    (child) => {
                        if (typeof child === "string") {
                            return child.replace(
                                "${agencyName}",
                                selectedAgencyData?.name || "CENTURY 21",
                            );
                        }
                        return child;
                    },
                );

                return <p className={style.paragraph}>{processedChildren}</p>;
            }

            return <p className={style.paragraph}>{children}</p>;
        },
        [BLOCKS.UL_LIST]: (_, children) => (
            <ul className={style.list}>{children}</ul>
        ),
        [BLOCKS.OL_LIST]: (_, children) => (
            <ol className={style.list}>{children}</ol>
        ),
        [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
        [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
        [BLOCKS.HEADING_3]: (_, children) => (
            <div className={style.heading}>
                <Typography type="heading-5-bold">{children}</Typography>
            </div>
        ),
        [BLOCKS.HEADING_4]: (_, children) => (
            <Typography type="heading-6-bold">{children}</Typography>
        ),
        [INLINES.ENTRY_HYPERLINK]: (
            {
                data: {
                    target: { fields },
                },
            },
            children,
        ) => {
            return fields ? (
                fields?.fileLink ? (
                    <a
                        href={`https:${fields?.fileLink[locale]?.fields?.file[locale]?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {children}
                    </a>
                ) : (
                    <Link to={`${fields.slug[locale]}`}>{children}</Link>
                )
            ) : (
                children
            );
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node) => {
            if (
                node.nodeType == "embedded-entry-block" &&
                node.data?.target?.fields?.fileLink
            ) {
                return (
                    <a
                        href={`https:${node.data.target.fields?.fileLink[locale]?.fields?.file[locale]?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Button color="primary">
                            {node.data.target.fields?.linkTitle[locale]}
                        </Button>
                    </a>
                );
            }
            return Render(node.data.target, locale);
        },
        [INLINES.EMBEDDED_ENTRY]: (node) => {
            if (
                node.nodeType == "embedded-entry-inline" &&
                node.data?.target?.fields?.fileLink
            ) {
                return (
                    <a
                        href={`https:${node.data.target.fields?.fileLink[locale]?.fields?.file[locale]?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {node.data.target.fields?.linkTitle[locale]}
                    </a>
                );
            }
            return Render(node.data.target, locale);
        },
    },
    /* eslint-enable */
});
const render = (content, key, locale, { isFirstRichTextRender, pageData }) => {
    const countryCode = (
        pageData?.country ||
        process.env.GATSBY_COUNTRY_CODE ||
        ""
    ).toUpperCase();

    let agencyApiId = null;
    if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        agencyApiId = urlParams.get("agency_api_id");
    }

    const selectedAgencyData = pageData?.allC21Agency?.find(
        (node) => node.api_id === agencyApiId,
    );

    const options = richTextBuilder(locale, selectedAgencyData);

    return (
        <>
            <Container className={style.container} key={key}>
                {(content?.text?.raw &&
                    documentToReactComponents(
                        JSON.parse(content.text.raw),
                        options,
                    )) ||
                    ""}
            </Container>

            {selectedAgencyData && isFirstRichTextRender && (
                <>
                    <Section>
                        <AgencyInformation
                            agency={selectedAgencyData}
                            fallbackImage={
                                pageData?.agencyFallbackImage?.childImageSharp
                            }
                            country={countryCode}
                            locale={locale}
                            title={t`form.estimation.map.title`.toUpperCase()}
                            isEstimationSuccessPage={
                                pageData?.isEstimationSuccessPage
                            }
                        />
                    </Section>
                    <Slider
                        locale={locale}
                        templateId={
                            process.env.GATSBY_PUBLIC_TRUSTPILOT_CAROUSEL_ID
                        }
                        businessUnitId={
                            process.env
                                .GATSBY_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID
                        }
                        reviewLanguages={"nl,fr"}
                        dataHeight={"240px"}
                        dataWidth={"100%"}
                        dataStars={"4,5"}
                        dataTheme={"light"}
                        dataTrustPilotId={selectedAgencyData.accountNumber}
                        dataSchemaType={"LocalBusiness"}
                        dataAllowRobots
                    />
                </>
            )}
        </>
    );
};

const condition = (content) =>
    content?.internal?.type === CONTENTFUL_TYPES.RichText;

export default createContentfulRenderer(condition, render);
